import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { useSession } from '@platform/api-client/react';
import {
  REVIEW_TASK_KINDS,
  REVIEW_TASK_STATUSES,
  type ReviewTaskKind,
  type ReviewTaskStatus,
} from '@platform/contracts';

import { ReviewScreen } from '../../features/review/review-screen';
import type { ReviewQueryState } from '../../features/review/review-view';
import { REVIEW_PERMISSIONS } from '../../features/review/types';
import { RequirePermission } from '../../lib/session/guards';

/** The filters, in the URL, so "the open price reviews" is a link a supervisor can send. */
function parseSearch(search: Record<string, unknown>): ReviewQueryState {
  const kind = String(search['kind'] ?? '');
  const status = String(search['status'] ?? '');
  return {
    kind: (REVIEW_TASK_KINDS as readonly string[]).includes(kind) ? (kind as ReviewTaskKind) : '',
    status: (REVIEW_TASK_STATUSES as readonly string[]).includes(status)
      ? (status as ReviewTaskStatus)
      : '',
  };
}

export const Route = createFileRoute('/review/')({
  validateSearch: parseSearch,
  component: ReviewPage,
});

/**
 * `/review` — the staff review queue (MASTER_PLAN M6.9 UI, FR-INS-09).
 *
 * `insurance.review` opens it and covers every action on it. The drawer links into
 * `/appointments/:id`, which is behind `appointments.read`; a role holding the review key
 * but not that one gets the queue and a link that refuses, which is the server's decision
 * to make and not this screen's (rules §17.2).
 */
function ReviewPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const session = useSession();
  const timeZone = session.data?.tenant?.timeZone ?? 'America/Los_Angeles';

  return (
    <RequirePermission keys={[REVIEW_PERMISSIONS.review]}>
      <ReviewScreen
        query={search}
        timeZone={timeZone}
        onQueryChange={(next) => {
          void navigate({ to: '/review', search: next, replace: true });
        }}
        renderAppointmentLink={(appointmentId, children) => (
          <Link
            to="/appointments/$appointmentId"
            params={{ appointmentId }}
            className="font-medium text-primary-accessible underline underline-offset-4 hover:text-primary-accessible-hover"
          >
            {children}
          </Link>
        )}
      />
    </RequirePermission>
  );
}
